import { useEffect, useMemo, useRef } from 'react';

/**
 * Helper function to compare two arrays (order-independent)
 * @param {Array} arr1 - First array
 * @param {Array} arr2 - Second array
 * @returns {boolean} - True if arrays contain the same elements
 */
const arraysEqual = (arr1, arr2) => {
    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();
    return JSON.stringify(sorted1) === JSON.stringify(sorted2);
};

/**
 * Custom hook to manage island filter state and logic
 * Handles:
 * - Creating extended island options with "All" option for admins
 * - Default island selection from client config
 * - Island restrictions (restrictedToOneIsland)
 * - "All" vs specific islands selection logic
 * - Validation and cleanup of invalid selections
 */
export const useIslandFilter = (
    islandOptions,
    currentIslands,
    isAdmin,
    setIslands,
    single = false
) => {
    const prevIslandsRef = useRef(null);

    // Create extended island options with proper sorting
    const extendedIslandOptions = useMemo(() => {
        // Add 'All' option only for admins as the first item
        let regions = isAdmin ? [{ name: 'All', id: 0 }, ...islandOptions] : islandOptions;

        regions = regions.map(e => ({
            ...e,
            value: e.id
        }));

        // Sort by name, but keep 'All' first if it exists
        if (isAdmin) {
            const allOption = regions.find(r => r.id === 0);
            const otherRegions = regions.filter(r => r.id !== 0);

            otherRegions.sort((a, b) => {
                if (a.name < b.name) {
                    return -1;
                }
                if (a.name > b.name) {
                    return 1;
                }
                return 0;
            });

            return [allOption, ...otherRegions];
        }

        // For non-admins, just sort normally
        regions.sort((a, b) => {
            if (a.name < b.name) {
                return -1;
            }
            if (a.name > b.name) {
                return 1;
            }
            return 0;
        });

        return regions;
    }, [islandOptions, isAdmin]);

    // Get default island IDs from client config
    const getDefaultIslandIds = () => {
        // In single-select mode keep at most one island selected
        const clamp = ids => (single ? ids.slice(0, 1) : ids);

        if (extendedIslandOptions.length === 0) {
            return isAdmin ? ['0'] : [];
        }

        const defaultIsland = window.clientData?.defaultIsland;

        // Handle old format (string) and new format (array)
        let islandNames = [];
        if (typeof defaultIsland === 'string' && defaultIsland.length > 0) {
            islandNames = [defaultIsland];
        } else if (Array.isArray(defaultIsland) && defaultIsland.length > 0) {
            islandNames = defaultIsland;
        }

        // If no default islands configured, use "All" for admins, first island for others
        if (islandNames.length === 0) {
            return isAdmin ? ['0'] : [String(extendedIslandOptions[0]?.id)];
        }

        // Convert island names from config to IDs
        const defaultIds = islandNames
            .map(name => {
                const island = extendedIslandOptions.find(opt => opt.name === name);
                return island ? String(island.id) : null;
            })
            .filter(id => id !== null);

        return defaultIds.length > 0
            ? clamp(defaultIds)
            : isAdmin
            ? ['0']
            : [String(extendedIslandOptions[0]?.id)];
    };

    // Handle "All" selection logic
    const handleAllSelection = (curr, prev) => {
        const hasAll = curr.includes('0');
        const selectedSpecific = curr.filter(id => id !== '0');

        // If both 'All' and specific islands are selected, determine which to keep
        if (hasAll && selectedSpecific.length > 0) {
            const prevHasAll = prev.includes('0');
            const prevHasSpecific = prev.some(id => id !== '0');

            // If user just clicked a specific island while 'All' was selected, keep only specific
            // If user just clicked 'All' while specific islands were selected, keep only 'All'
            return prevHasAll && !prevHasSpecific ? selectedSpecific : ['0'];
        }

        return null; // No change needed
    };

    // Check if all specific islands are selected (should auto-select "All")
    const shouldAutoSelectAll = curr => {
        if (!isAdmin) {
            return false;
        }

        const validSpecificSet = new Set(
            extendedIslandOptions.map(o => String(o.id)).filter(id => id !== '0')
        );

        const selectedSpecificSet = new Set(
            curr.filter(id => id !== '0' && validSpecificSet.has(id))
        );

        return (
            !curr.includes('0') &&
            validSpecificSet.size > 0 &&
            selectedSpecificSet.size === validSpecificSet.size
        );
    };

    // Main effect to handle island selection logic
    useEffect(() => {
        if (extendedIslandOptions.length === 0) {
            return;
        }

        // Initialize if islands is null or undefined
        if (!currentIslands) {
            const defaultIds = getDefaultIslandIds();
            setIslands(defaultIds);
            return;
        }

        // Handle island restrictions - force to default island(s)
        if (window.clientData?.restrictedToOneIsland) {
            const defaultIds = getDefaultIslandIds();

            if (!arraysEqual(currentIslands, defaultIds)) {
                setIslands(defaultIds);
            }
            return;
        }

        // Validate that currentIslands is an array
        if (!Array.isArray(currentIslands)) {
            setIslands(getDefaultIslandIds());
            return;
        }

        const curr = currentIslands.map(String);
        const prev = prevIslandsRef.current || [];

        // Handle "All" selection logic
        const allSelectionResult = handleAllSelection(curr, prev);
        if (allSelectionResult) {
            if (!arraysEqual(allSelectionResult, curr)) {
                setIslands(allSelectionResult);
                prevIslandsRef.current = allSelectionResult;
                return;
            }
        }

        // Auto-select "All" if all specific islands are selected (admin only)
        if (shouldAutoSelectAll(curr)) {
            setIslands(['0']);
            prevIslandsRef.current = ['0'];
            return;
        }

        // Validate and clean up invalid island IDs
        const validSpecificSet = new Set(
            extendedIslandOptions.map(o => String(o.id)).filter(id => id !== '0')
        );
        const validIds = new Set(isAdmin ? ['0', ...validSpecificSet] : [...validSpecificSet]);
        const cleaned = curr.filter(id => validIds.has(id));

        // If no valid islands remain, reset to defaults
        if (cleaned.length === 0) {
            setIslands(getDefaultIslandIds());
            return;
        }

        // In single-select mode collapse any multi-selection (e.g. legacy URLs) to one
        if (single && cleaned.length > 1) {
            setIslands([cleaned[0]]);
            return;
        }

        // Update if we cleaned up invalid IDs
        if (!arraysEqual(cleaned, curr)) {
            setIslands(cleaned);
            return;
        }

        // Update the previous reference for next comparison
        prevIslandsRef.current = curr;
    }, [currentIslands, extendedIslandOptions, isAdmin, setIslands, single]);

    return extendedIslandOptions;
};
